// Shared code: Shopify connection, cart, favorites, header/menu/search/footer, helpers.
(function () {
  const S = window.SITE;
  const ENDPOINT = `https://${S.shopDomain}/api/${S.apiVersion}/graphql.json`;

  // ---------- helpers ----------
  const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
  const qs = (name) => new URLSearchParams(location.search).get(name);
  const img = (url, width) => (url ? `${url}${url.includes("?") ? "&" : "?"}width=${width}` : "");
  const productUrl = (handle) => `/products/${encodeURIComponent(handle)}`;
  const shopUrl = (type) => (type ? `/collections/all?type=${encodeURIComponent(type)}` : "/collections/all");

  const ICONS = {
    menu: '<svg class="icon" viewBox="0 0 24 24"><path d="M3 6h18M3 12h18M3 18h18"/></svg>',
    close: '<svg class="icon" viewBox="0 0 24 24"><path d="M5 5l14 14M19 5L5 19"/></svg>',
    search: '<svg class="icon" viewBox="0 0 24 24"><circle cx="10.5" cy="10.5" r="6.5"/><path d="M15.5 15.5L21 21"/></svg>',
    fav: '<svg class="icon" viewBox="0 0 24 24"><path d="M6 3h12v18l-6-5-6 5z"/></svg>',
    bag: '<svg class="icon" viewBox="0 0 24 24"><path d="M4 8h16v12H4zM8.5 8V6a3.5 3.5 0 017 0v2"/></svg>',
    left: '<svg class="icon" viewBox="0 0 24 24"><path d="M15 4l-8 8 8 8"/></svg>',
    right: '<svg class="icon" viewBox="0 0 24 24"><path d="M9 4l8 8-8 8"/></svg>',
  };

  function money(m) {
    if (!m || Number(m.amount) === 0) return S.priceOnRequest;
    const n = Number(m.amount);
    return new Intl.NumberFormat(m.currencyCode === "TRY" ? "tr-TR" : "en-IE", { style: "currency", currency: m.currencyCode, maximumFractionDigits: n % 1 ? 2 : 0 }).format(n);
  }

  function store(key, value) {
    try {
      if (value === undefined) return localStorage.getItem(key);
      if (value === null) localStorage.removeItem(key); else localStorage.setItem(key, value);
    } catch (e) { return null; }
  }

  // Split a Shopify description into { story, materials[] } — lines after a "—" line are materials.
  function splitDescription(html) {
    const parts = String(html || "").split(/<p>\s*—\s*<\/p>/);
    const tmp = document.createElement("div");
    tmp.innerHTML = parts[1] || "";
    const materials = [...tmp.querySelectorAll("p")]
      .map((p) => p.textContent.trim().replace(/\.$/, ""))
      .filter((t) => t && !/^handmade/i.test(t));
    return { story: parts[0], details: parts[1] || "", materials };
  }

  async function shopify(query, variables = {}) {
    const res = await fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Shopify-Storefront-Access-Token": S.storefrontToken },
      body: JSON.stringify({ query, variables }),
    });
    const json = await res.json();
    if (json.errors) throw new Error(json.errors.map((e) => e.message).join("; "));
    return json.data;
  }

  // ---------- products ----------
  const CARD_FIELDS = `
    id handle title productType availableForSale descriptionHtml
    priceRange { minVariantPrice { amount currencyCode } }
    images(first: 6) { nodes { url altText } }
    variants(first: 2) { nodes { id availableForSale currentlyNotInStock } }
  `;

  async function getProducts({ type, search, sortKey = "CREATED_AT", reverse = true, first = 50 } = {}) {
    const q = [type && `product_type:"${type.replace(/"/g, "")}"`, search].filter(Boolean).join(" ") || null;
    const data = await shopify(
      `query($first:Int!, $query:String, $sortKey:ProductSortKeys, $reverse:Boolean) {
        products(first:$first, query:$query, sortKey:$sortKey, reverse:$reverse) { nodes { ${CARD_FIELDS} } } }`,
      { first, query: q, sortKey, reverse }
    );
    return data.products.nodes;
  }

  async function getProduct(handle) {
    const data = await shopify(
      `query($handle:String!) { product(handle:$handle) {
        id handle title productType descriptionHtml availableForSale
        priceRange { minVariantPrice { amount currencyCode } }
        options { name values }
        images(first: 20) { nodes { url altText } }
        variants(first: 100) { nodes { id title availableForSale currentlyNotInStock price { amount currencyCode } selectedOptions { name value } } }
      } }`,
      { handle }
    );
    return data.product;
  }

  // Editorial (model) photos = every image after the first one
  const editorialImages = (products) => products.flatMap((p) => p.images.nodes.slice(1).map((im) => ({ ...im, product: p })));

  // ---------- favorites (kept in this browser) ----------
  const favs = () => { try { return JSON.parse(store("favorites") || "[]"); } catch (e) { return []; } };
  const isFav = (h) => favs().includes(h);
  function toggleFav(h) {
    const list = favs();
    const next = list.includes(h) ? list.filter((x) => x !== h) : [...list, h];
    store("favorites", JSON.stringify(next));
    document.querySelectorAll(`[data-fav="${CSS.escape(h)}"]`).forEach((b) => b.setAttribute("aria-pressed", next.includes(h)));
    toast(next.includes(h) ? "Saved to favorites" : "Removed from favorites");
  }

  function productCard(p, { badge } = {}) {
    const [a, b] = p.images.nodes;
    const single = p.variants.nodes.length === 1;
    const v = p.variants.nodes[0];
    const soldOut = !p.availableForSale || p.variants.nodes.every((x) => !x.availableForSale || x.currentlyNotInStock);
    const canQuickAdd = !soldOut && single && v?.availableForSale;
    return `
      <div class="card">
        <div class="card-media-wrap">
          <a class="card-media" href="${productUrl(p.handle)}">
            ${a ? `<img src="${img(a.url, 800)}" alt="${esc(a.altText || p.title)}" loading="lazy">` : ""}
            ${b ? `<img class="alt" src="${img(b.url, 800)}" alt="" loading="lazy">` : ""}
            ${soldOut ? `<span class="card-badge">Sold out</span>` : badge ? `<span class="card-badge">${esc(badge)}</span>` : ""}
          </a>
          ${canQuickAdd ? `<button class="quick-add" data-quick-add="${esc(v.id)}">+ Add to cart</button>` : ""}
        </div>
        <div class="card-info">
          <a class="card-name" href="${productUrl(p.handle)}">${esc(p.title)}</a>
          <button class="fav" data-fav="${esc(p.handle)}" aria-pressed="${isFav(p.handle)}" aria-label="Save to favorites">${ICONS.fav}</button>
          <span class="card-price">${money(p.priceRange.minVariantPrice)}</span>
        </div>
      </div>`;
  }

  const skeletons = (n) => Array.from({ length: n }, () => `<div class="skeleton"></div>`).join("");

  // ---------- cart ----------
  const CART_FIELDS = `
    id checkoutUrl totalQuantity
    cost { subtotalAmount { amount currencyCode } }
    lines(first: 100) { nodes { id quantity
      cost { totalAmount { amount currencyCode } }
      merchandise { ... on ProductVariant { id title image { url } product { title handle featuredImage { url } } } }
    } }
  `;
  let cart = null;

  async function loadCart() {
    const id = store("cartId");
    if (!id) return null;
    try {
      cart = (await shopify(`query($id:ID!) { cart(id:$id) { ${CART_FIELDS} } }`, { id })).cart;
      if (!cart) store("cartId", null);
    } catch (e) { cart = null; }
    return cart;
  }

  async function addToCart(variantId, quantity = 1) {
    const lines = [{ merchandiseId: variantId, quantity }];
    const data = cart
      ? (await shopify(`mutation($id:ID!, $lines:[CartLineInput!]!) { cartLinesAdd(cartId:$id, lines:$lines) { cart { ${CART_FIELDS} } userErrors { message } } }`, { id: cart.id, lines })).cartLinesAdd
      : (await shopify(`mutation($lines:[CartLineInput!]!) { cartCreate(input:{ lines:$lines }) { cart { ${CART_FIELDS} } userErrors { message } } }`, { lines })).cartCreate;
    if (data.userErrors.length) throw new Error(data.userErrors[0].message);
    cart = data.cart;
    store("cartId", cart.id);
    renderCart();
    openPanel("drawer");
  }

  async function updateLine(lineId, quantity) {
    const data = quantity > 0
      ? (await shopify(`mutation($id:ID!, $lines:[CartLineUpdateInput!]!) { cartLinesUpdate(cartId:$id, lines:$lines) { cart { ${CART_FIELDS} } } }`, { id: cart.id, lines: [{ id: lineId, quantity }] })).cartLinesUpdate
      : (await shopify(`mutation($id:ID!, $ids:[ID!]!) { cartLinesRemove(cartId:$id, lineIds:$ids) { cart { ${CART_FIELDS} } } }`, { id: cart.id, ids: [lineId] })).cartLinesRemove;
    cart = data.cart;
    renderCart();
  }

  function renderCart() {
    document.querySelectorAll("[data-cart-count]").forEach((el) => (el.textContent = cart ? cart.totalQuantity : 0));
    const body = document.querySelector(".drawer-body");
    const foot = document.querySelector(".drawer-foot");
    if (!body) return;
    if (!cart || !cart.lines.nodes.length) {
      body.innerHTML = `<div class="empty"><p>Your cart is empty</p><a class="btn" href="/collections/all">Discover the collection</a></div>`;
      foot.hidden = true;
      return;
    }
    foot.hidden = false;
    body.innerHTML = cart.lines.nodes.map((l) => {
      const v = l.merchandise;
      const pic = (v.image || v.product.featuredImage || {}).url;
      return `
        <div class="line">
          <a href="${productUrl(v.product.handle)}">${pic ? `<img src="${img(pic, 240)}" alt="">` : ""}</a>
          <div>
            <a class="line-name" href="${productUrl(v.product.handle)}">${esc(v.product.title)}</a>
            ${v.title !== "Default Title" ? `<div class="muted">${esc(v.title)}</div>` : ""}
            <div>${money(l.cost.totalAmount)}</div>
            <span class="qty"><button data-qty="${l.id}" data-n="${l.quantity - 1}" aria-label="Decrease">−</button><span>${l.quantity}</span><button data-qty="${l.id}" data-n="${l.quantity + 1}" aria-label="Increase">+</button></span>
            <button class="remove" data-qty="${l.id}" data-n="0">Remove</button>
          </div>
        </div>`;
    }).join("");
    foot.querySelector("[data-subtotal]").textContent = money(cart.cost.subtotalAmount);
    foot.querySelector("[data-checkout]").href = cart.checkoutUrl;
  }

  // ---------- panels (menu, search, cart) ----------
  function openPanel(name) {
    closePanels();
    document.querySelector(`.${name}`).classList.add("open");
    if (name === "drawer") document.querySelector(".backdrop").classList.add("open");
    if (name === "search") setTimeout(() => document.querySelector(".search input").focus(), 50);
    document.body.classList.add("no-scroll");
  }
  function closePanels() {
    document.querySelectorAll(".menu, .search, .drawer, .backdrop").forEach((el) => el.classList.remove("open"));
    document.body.classList.remove("no-scroll");
  }

  function toast(msg) {
    let t = document.querySelector(".toast");
    if (!t) { t = document.createElement("div"); t.className = "toast"; document.body.appendChild(t); }
    t.textContent = msg; t.classList.add("show");
    clearTimeout(t._h); t._h = setTimeout(() => t.classList.remove("show"), 2400);
  }

  // Wire the arrows of every .carousel on the page
  function initCarousels(root = document) {
    root.querySelectorAll(".carousel").forEach((c) => {
      const track = c.querySelector(".carousel-track");
      c.parentElement.querySelectorAll("[data-car]").forEach((btn) => {
        btn.onclick = () => track.scrollBy({ left: (btn.dataset.car === "next" ? 1 : -1) * track.clientWidth * 0.75, behavior: "smooth" });
      });
    });
  }

  // ---------- layout ----------
  const logo = `<img src="${window.ASSET_BASE}logo.png" alt="${esc(S.brand)}">`;

  function renderLayout() {
    const top = document.createElement("div");
    top.innerHTML = `
      ${S.announcement ? `<div class="announcement">${esc(S.announcement)}</div>` : ""}
      <header class="site-header">
        <div class="left"><button data-open="menu" aria-label="Menu">${ICONS.menu}</button></div>
        <a class="logo" href="/" aria-label="${esc(S.brand)} — home">${logo}</a>
        <div class="right">
          <button data-open="search" aria-label="Search">${ICONS.search}</button>
          <a class="hide-sm" href="/collections/all?view=favorites" aria-label="Favorites">${ICONS.fav}</a>
          <button class="bag-btn" data-open="drawer" aria-label="Cart">${ICONS.bag}<span data-cart-count>0</span></button>
        </div>
      </header>
      <nav class="menu" aria-label="Main menu">
        <div class="menu-head">
          <div><button data-close aria-label="Close">${ICONS.close}</button></div>
          <a class="logo" href="/">${logo}</a><div></div>
        </div>
        <div class="menu-body">
          <div class="menu-links">
            <a href="/collections/all">New in</a>
            <a href="/collections/all">All jewelry</a>
            <span class="gap"></span>
            ${S.categories.map((c) => `<a href="${shopUrl(c.type)}">${esc(c.label)}</a>`).join("")}
            <span class="gap"></span>
            <a href="/collections/all?view=favorites">Favorites</a>
          </div>
          <div class="menu-tiles" data-menu-tiles></div>
        </div>
      </nav>
      <div class="search" role="dialog" aria-label="Search">
        <div class="search-bar">${ICONS.search}<input type="search" placeholder="Search" aria-label="Search products"><button data-close aria-label="Close">${ICONS.close}</button></div>
        <div class="search-results"><div class="grid" data-search-results></div></div>
      </div>`;
    document.body.prepend(...top.children);

    const bottom = document.createElement("div");
    bottom.innerHTML = `
      <footer class="site-footer">
        <div class="footer-top">
          <div><h3>${esc(S.brand)}</h3><p class="muted" style="max-width:420px">${esc(S.shopAllText)}</p></div>
          <div><h3>Shop</h3><ul><li><a href="/collections/all">All jewelry</a></li>${S.categories.map((c) => `<li><a href="${shopUrl(c.type)}">${esc(c.label)}</a></li>`).join("")}</ul></div>
          <div><h3>Help</h3><ul>
            <li><a href="/collections/all?view=favorites">Favorites</a></li>
            <li><a href="/collections/all?view=legal&doc=iletisim">İletişim</a></li>
            ${S.instagram ? `<li><a href="${esc(S.instagram)}" target="_blank" rel="noopener">Instagram</a></li>` : ""}
          </ul></div>
          <div><h3>Yasal</h3><ul>
            <li><a href="/collections/all?view=legal&doc=mesafeli-satis">Mesafeli Satış Sözleşmesi</a></li>
            <li><a href="/collections/all?view=legal&doc=on-bilgilendirme">Ön Bilgilendirme Formu</a></li>
            <li><a href="/collections/all?view=legal&doc=iade">İptal ve İade Koşulları</a></li>
            <li><a href="/collections/all?view=legal&doc=kvkk">Gizlilik ve KVKK</a></li>
          </ul></div>
        </div>
        <div class="footer-logo">${logo}</div>
        <div class="footer-bottom"><span>© ${new Date().getFullYear()} ${esc(S.brand)}</span>${S.footerNote ? `<span>${esc(S.footerNote)}</span>` : ""}</div>
      </footer>
      <div class="backdrop" data-close></div>
      <aside class="drawer" aria-label="Cart">
        <div class="drawer-head"><span>Cart (<span data-cart-count>0</span>)</span><button data-close aria-label="Close">${ICONS.close}</button></div>
        <div class="drawer-body"></div>
        <div class="drawer-foot" hidden>
          <div class="row"><span>Subtotal</span><span data-subtotal></span></div>
          <p class="muted" style="font-size:12px">Shipping and taxes calculated at checkout</p>
          <a class="btn block" data-checkout href="#">Checkout</a>
        </div>
      </aside>`;
    document.body.append(...bottom.children);

    // Clicks anywhere on the page
    document.addEventListener("click", async (e) => {
      const t = e.target.closest("[data-open],[data-close],[data-qty],[data-fav],[data-quick-add]");
      if (!t) return;
      if (t.dataset.open) openPanel(t.dataset.open);
      else if (t.hasAttribute("data-close")) closePanels();
      else if (t.dataset.fav) { e.preventDefault(); toggleFav(t.dataset.fav); }
      else if (t.dataset.qty) { t.disabled = true; updateLine(t.dataset.qty, Number(t.dataset.n)).catch(() => toast("Could not update your cart")); }
      else if (t.dataset.quickAdd) {
        e.preventDefault();
        const label = t.textContent; t.textContent = "Adding…";
        try { await addToCart(t.dataset.quickAdd); } catch (err) { console.error(err); toast("Sorry, this could not be added"); }
        t.textContent = label;
      }
    });
    document.addEventListener("keydown", (e) => e.key === "Escape" && closePanels());

    // Live search
    const input = document.querySelector(".search input");
    const results = document.querySelector("[data-search-results]");
    let timer;
    input.addEventListener("input", () => {
      clearTimeout(timer);
      const term = input.value.trim();
      if (!term) { results.innerHTML = ""; return; }
      timer = setTimeout(async () => {
        results.innerHTML = skeletons(4);
        try {
          const found = await getProducts({ search: term, sortKey: "RELEVANCE", reverse: false, first: 12 });
          results.innerHTML = found.length ? found.map((p) => productCard(p)).join("") : `<p class="notice">No results for "${esc(term)}"</p>`;
        } catch (err) { results.innerHTML = `<p class="notice">Search is unavailable</p>`; }
      }, 300);
    });

    // Transparent header over the home hero
    const header = document.querySelector(".site-header");
    const hero = document.querySelector(".hero");
    if (hero) {
      document.body.classList.add("has-hero");
      const update = () => header.classList.toggle("transparent", window.scrollY < hero.offsetHeight - header.offsetHeight);
      update();
      window.addEventListener("scroll", update, { passive: true });
    }
  }

  // Fill the menu's photo tiles (one per category) once products are known
  async function fillMenuTiles() {
    try {
      const products = await getProducts({ first: 50 });
      const tiles = S.categories.map((c) => {
        const p = products.find((x) => x.productType === c.type);
        const pic = p && (p.images.nodes[1] || p.images.nodes[0]);
        return pic ? `<a href="${shopUrl(c.type)}"><div class="ph"><img src="${img(pic.url, 500)}" alt="" loading="lazy"></div>${esc(c.label)}</a>` : "";
      });
      document.querySelector("[data-menu-tiles]").innerHTML = tiles.join("");
      return products;
    } catch (e) { return []; }
  }

  // Font theme is set in config.js
  function applyFont() {
    try { sessionStorage.removeItem("fontPreview"); } catch (e) { /* storage blocked */ }
    document.body.classList.add(`font-${S.font || "inter"}`);
  }

  applyFont();
  renderLayout();
  renderCart();
  loadCart().then(renderCart);
  const allProducts = fillMenuTiles();

  window.Store = {
    S, ICONS, esc, qs, img, money, productUrl, shopify, shopUrl, splitDescription,
    getProducts, getProduct, editorialImages, allProducts,
    productCard, skeletons, addToCart, toast, isFav, initCarousels,
  };
})();
